import { useMemo, useState } from 'react';
import {
  Anchor,
  Box,
  Collapse,
  Flex,
  Group,
  Paper,
  Popover,
  Progress,
  Stack,
  Switch,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { PiCaretDown, PiCaretRight } from 'react-icons/pi';
import { useConfirmation } from '@/hooks/useConfirmation';
import { useAuth } from '@/models/auth';
import { useHasAnyPermission } from '@/models/permissions';
import { TOffer, useOfferActivity, useUpdateOffer } from '@/models/offers';
import OfferActivityFeed from '../OfferActivityFeed';
import ServicesWidget from '../ServicesWidget';
import { useMockSplitSchedule } from '../mock/mockSplitSchedule';
import { payingCurrencyOfOffer } from '../payments/scheduleCurrency';
import SimulateInvoiceCompanySelection from '../../sales/SimulateInvoiceCompanySelection';
import SimulateSumsubWebhook, {
  SIMULATABLE_KYC_STATUSES,
} from '@/components/SimulateSumsubWebhook';

// Mock "delivery started" flag — gates the Delivery Progression widget until real
// Operations wiring lands. Persisted per-offer so the demo stays stable across reloads.
function useMockDeliveryStarted(offerId: number) {
  const key = `mock:deliveryStarted:${offerId}`;
  const [started, setStarted] = useState(
    () =>
      typeof localStorage !== 'undefined' && localStorage.getItem(key) === '1',
  );
  const set = (v: boolean) => {
    setStarted(v);
    try {
      localStorage.setItem(key, v ? '1' : '0');
    } catch {
      /* ignore */
    }
  };
  return [started, set] as const;
}

export default function GeneralTab({ offer }: { offer: TOffer }) {
  const { data: activity } = useOfferActivity(offer.id);
  const { user } = useAuth();
  const currentUserId = user?.id != null ? Number(user.id) : undefined;
  const canAccessAllOffers = useHasAnyPermission(['offers:access-all']);
  const [deliveryStarted, setDeliveryStarted] = useMockDeliveryStarted(
    offer.id,
  );

  return (
    <Flex gap="md" align="flex-start" wrap="wrap">
      {/* Services need the width, so the right column starts wider and grows
          twice as fast; activity only creeps up to its 640px ceiling. */}
      <Box style={{ flex: '1 1 360px', maxWidth: 640, minWidth: 0 }}>
        <Stack gap="md">
          <Box>
            <OfferActivityFeed
              offerId={offer.id}
              activity={activity || []}
              currentUserId={currentUserId}
              canMutate={currentUserId === offer.createdBy || canAccessAllOffers}
            />
          </Box>
        </Stack>
      </Box>

      <Box style={{ flex: '2 1 520px', minWidth: 0 }}>
        <Stack gap="md">
          {offer.accepted && <PaymentProgression offer={offer} />}
          {deliveryStarted && <DeliveryProgression />}
          <Paper withBorder p="sm">
            <ExpiryControl offer={offer} />
          </Paper>
          <ServicesWidget offer={offer} />
          <DevTools
            offer={offer}
            deliveryStarted={deliveryStarted}
            onToggleDelivery={setDeliveryStarted}
          />
        </Stack>
      </Box>
    </Flex>
  );
}

function ExpiryControl({ offer }: { offer: TOffer }) {
  const update = useUpdateOffer();
  const { confirm, ConfirmationDialog } = useConfirmation();
  const [opened, setOpened] = useState(false);
  const disabled = offer.accepted;

  const expiry = offer.expiresAt ? offer.expiresAt.slice(0, 10) : null;
  const formatted = expiry
    ? new Date(expiry).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : null;

  // Persist as end-of-day so selecting today still satisfies the backend's
  // `min('now')` validation (and matches the "expires at end of that day" intent).
  const saveExpiry = async (str: string | null) => {
    try {
      await update.mutateAsync({
        offerId: offer.id,
        expiresAt: str ? new Date(`${str}T23:59:59`).toISOString() : null,
      });
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `Failed to update expiry: ${e}`,
      });
    }
  };

  const handlePick = async (value: string | null) => {
    setOpened(false);
    if (value) await saveExpiry(value);
  };

  const handleClear = async () => {
    const ok = await confirm({
      title: 'Clear expiry',
      message:
        'Remove the expiry date? The offer will no longer expire automatically.',
      confirmButtonText: 'Clear',
    });
    if (ok) await saveExpiry(null);
  };

  return (
    <Group justify="space-between" gap="xs" align="center" wrap="nowrap">
      <ConfirmationDialog />
      <Text fw={500} fz="sm">
        Expiry
      </Text>
      <Group gap="xs" align="center" style={{ flex: 1 }} wrap="nowrap">
        {disabled ? (
          <Text fz="sm">{formatted ?? 'Never'}</Text>
        ) : (
          <Popover
            opened={opened}
            onChange={setOpened}
            position="bottom-start"
            withinPortal
            trapFocus
          >
            <Popover.Target>
              <Anchor
                component="button"
                type="button"
                fz="sm"
                underline="always"
                c={formatted ? undefined : 'dimmed'}
                onClick={() => setOpened((o) => !o)}
              >
                {formatted ?? 'never'}
              </Anchor>
            </Popover.Target>
            <Popover.Dropdown p="xs">
              <DatePicker
                value={expiry}
                onChange={handlePick}
                minDate={new Date()}
              />
            </Popover.Dropdown>
          </Popover>
        )}
        {!disabled && expiry && (
          <Anchor
            component="button"
            type="button"
            fz="xs"
            c="red"
            onClick={handleClear}
            disabled={update.isPending}
          >
            clear
          </Anchor>
        )}
      </Group>
    </Group>
  );
}

function PaymentProgression({ offer }: { offer: TOffer }) {
  // When a mock split is approved, progression is driven by the installment
  // schedule (mock payments) rather than the real invoice roll-up.
  const schedule = useMockSplitSchedule((s) => s.schedules[offer.id]);
  const mockEntries = useMemo<
    [string, { paid: number; outstanding: number }][]
  >(() => {
    if (schedule?.status !== 'approved') return [];
    const cur = payingCurrencyOfOffer(offer);
    const total = schedule.installments.reduce(
      (a, i) => a + (i.dueAmount ?? 0),
      0,
    );
    const paid = schedule.installments.reduce(
      (a, i) => a + (i.issued && i.paid ? (i.dueAmount ?? 0) : 0),
      0,
    );
    return [[cur, { paid, outstanding: total - paid }]];
  }, [schedule, offer]);

  const totalEntries =
    mockEntries.length > 0
      ? mockEntries
      : Object.entries(offer.currencyTotals || {});

  return (
    <Paper withBorder p="sm">
      <Text fw={500} fz="sm" mb="xs">
        Payment progression
      </Text>
      {totalEntries.length > 0 ? (
        <Stack gap={6}>
          {totalEntries.map(([cur, v]) => {
            const total = v.paid + v.outstanding;
            const pct = total > 0 ? (v.paid / total) * 100 : 0;
            return (
              <Stack key={cur} gap={2}>
                <Progress value={pct} color="teal" />
                <Group justify="space-between">
                  <Text fz="xs" c="dimmed">
                    {cur}
                  </Text>
                  <Text fz="xs">
                    {v.paid.toLocaleString()} paid / {total.toLocaleString()}{' '}
                    total
                  </Text>
                </Group>
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <Text fz="xs" c="dimmed">
          No payments yet.
        </Text>
      )}
    </Paper>
  );
}

function DeliveryProgression() {
  return (
    <Paper withBorder p="sm">
      <Text fw={500} fz="sm" mb="xs">
        Delivery progression
      </Text>
      <Text fz="xs" c="dimmed">
        Operations tracking in progress.
      </Text>
    </Paper>
  );
}

function DevTools({
  offer,
  deliveryStarted,
  onToggleDelivery,
}: {
  offer: TOffer;
  deliveryStarted: boolean;
  onToggleDelivery: (v: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const Icon = open ? PiCaretDown : PiCaretRight;
  return (
    <Paper withBorder p="sm">
      <UnstyledButton onClick={() => setOpen((o) => !o)}>
        <Group gap={4}>
          <Icon size={12} color="var(--mantine-color-dimmed)" />
          <Text fz="xs" fw={500} c="dimmed">
            Developer tools
          </Text>
        </Group>
      </UnstyledButton>
      <Collapse expanded={open}>
        <Box pt="sm">
          <Stack gap="md">
            <Switch
              size="xs"
              label="Delivery started (mock)"
              checked={deliveryStarted}
              onChange={(e) => onToggleDelivery(e.currentTarget.checked)}
            />
            <SimulateInvoiceCompanySelection offerId={offer.id} />
            {offer.kycSessionId &&
              offer.kycStatus &&
              SIMULATABLE_KYC_STATUSES.includes(offer.kycStatus) && (
                <SimulateSumsubWebhook
                  sessionId={offer.kycSessionId}
                  variant="inline"
                />
              )}
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}
